// src/useAccess.js
import { useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';

/**
 * Central access control hook (LIVE)
 *
 * Final access values:
 * locked | lifetime | beta | trialing | active | past_due | canceled
 */
export const useAccess = () => {
  const { currentUser, userDoc, settingsDoc, loading: authLoading } = useAuth();

  // Stripe status lives on users/{uid}.stripeStatus (set by webhook)
  const stripeStatusRaw = userDoc?.stripeStatus ? String(userDoc.stripeStatus).toLowerCase() : null;

  // Settings lives on users/{uid}/settings/main (LIVE via AuthContext)
  const rawAccess = settingsDoc?.access ? String(settingsDoc.access).toLowerCase() : 'locked';
  const isLifetime = settingsDoc?.lifetime === true || rawAccess === 'lifetime';

  const access = useMemo(() => {
    if (!currentUser) return 'locked';
    if (isLifetime) return 'lifetime';

    // Stripe has priority when not lifetime
    if (stripeStatusRaw) {
      if (stripeStatusRaw === 'trialing') return 'trialing';
      if (stripeStatusRaw === 'active') return 'active';
      if (stripeStatusRaw === 'past_due' || stripeStatusRaw === 'unpaid') return 'past_due';
      if (stripeStatusRaw === 'canceled') return 'canceled';
    }

    // Otherwise fall back to manual access flag (beta/locked/etc)
    return rawAccess || 'locked';
  }, [currentUser, isLifetime, stripeStatusRaw, rawAccess]);

  const loading = authLoading;

  const canUsePaidFeatures = useMemo(
    () => ['lifetime', 'beta', 'trialing', 'active'].includes(access),
    [access]
  );

  const accessLabel = useMemo(() => {
    switch (access) {
      case 'lifetime':
        return 'Lifetime Access';
      case 'beta':
        return 'Beta Access';
      case 'trialing':
        return 'Free trial';
      case 'active':
        return 'Active';
      case 'past_due':
        return 'Payment issue';
      case 'canceled':
        return 'Canceled';
      default:
        return 'Preview';
    }
  }, [access]);

  const openPaywall = useCallback(async () => {
    if (!currentUser) {
      window.location.href = '/login';
      return;
    }

    if (access === 'lifetime') {
      window.location.href = '/account';
      return;
    }

    try {
      const token = await currentUser.getIdToken();

      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to start checkout');

      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert(err?.message || 'Checkout failed');
    }
  }, [currentUser, access]);

  return {
    access,
    accessLabel,
    canUsePaidFeatures,
    loading,
    openPaywall,
  };
};





